import type { ITrack, ITrackItem } from "@designcombo/types";
import type { CompositionVideoShortsPropsType } from "@repo/types";
import { isSpeechCaptionItem } from "../on-screen-text-position";
import { generateId } from "../trackId";
import { regenerateCaptionsWithMaxWords } from "./index";

export interface RegroupCaptionTrackItemsOptions {
	captionTrack: ITrack;
	trackItemsMap: Record<string, ITrackItem>;
	maxWordsInPage: number;
	originalVideoData?: CompositionVideoShortsPropsType | null;
	currentShortId?: number | null;
	size?: { width: number; height: number };
	fps?: number;
}

export interface RegroupCaptionTrackResult {
	updatedTrack: ITrack;
	newTrackItems: ITrackItem[];
	removedItemIds: string[];
	updatedTrackItemsMap: Record<string, ITrackItem>;
	updatedTrackItemIds: string[];
}

/**
 * Re-chunks caption items on a caption track to match the requested `maxWordsInPage`.
 * Works for both Shorts mode (using originalVideoData if available) and Video Project / Design mode.
 */
export function regroupCaptionTrackItems(
	options: RegroupCaptionTrackItemsOptions,
): RegroupCaptionTrackResult {
	const {
		captionTrack,
		trackItemsMap,
		maxWordsInPage,
		originalVideoData,
		currentShortId,
		size = { width: 1080, height: 1920 },
		fps = 30,
	} = options;

	const maxWords = Math.max(1, Math.floor(maxWordsInPage));
	const existingItems = (captionTrack.items || [])
		.map((id) => trackItemsMap[id])
		.filter((item): item is ITrackItem => Boolean(item && item.type === "caption"));
	existingItems.sort((a, b) => a.display.from - b.display.from);

	// Guard: Never regroup on-screen text or media source badges
	if (existingItems.length > 0 && existingItems.some((item) => !isSpeechCaptionItem(item))) {
		return {
			updatedTrack: captionTrack,
			newTrackItems: existingItems,
			removedItemIds: [],
			updatedTrackItemsMap: trackItemsMap,
			updatedTrackItemIds: Object.keys(trackItemsMap),
		};
	}

	const removedItemIds = [...(captionTrack.items || [])];

	let newTrackItems: ITrackItem[] = [];

	// Branch 1: If original video data exists (Shorts mode), regenerate from source transcription
	if (originalVideoData?.video) {
		const targetShortId = currentShortId ?? originalVideoData.target;
		const targetShort = (originalVideoData.video as any).shorts?.find(
			(short: any) => short.id === targetShortId,
		);

		if (targetShort) {
			const regenerated = regenerateCaptionsWithMaxWords(
				originalVideoData.video as any,
				targetShort as any,
				originalVideoData.options?.contentOptions || {},
				maxWords,
				size,
				fps,
			);

			// Overlay styling details from the first existing caption item if present
			const sampleItem = existingItems[0];
			if (sampleItem?.details) {
				const { text: _t, words: _w, ...styleProps } =
					sampleItem.details as Record<string, unknown>;
				newTrackItems = regenerated.map((item) => ({
					...item,
					details: {
						...item.details,
						...styleProps,
						text: item.details.text,
						words: item.details.words,
					},
				}));
			} else {
				newTrackItems = regenerated;
			}
		}
	}

	// Branch 2: Fallback (Video Projects / compiled designs / no originalVideoData)
	if (newTrackItems.length === 0 && existingItems.length > 0) {
		const allWords: Array<{
			word: string;
			start: number;
			end: number;
			originalWord?: string;
			isEdited?: boolean;
		}> = [];

		for (const item of existingItems) {
			const itemWords = (item.details as any)?.words;
			if (Array.isArray(itemWords) && itemWords.length > 0) {
				for (const w of itemWords) {
					allWords.push({
						word: w.word,
						start: typeof w.start === "number" ? w.start : item.display.from,
						end: typeof w.end === "number" ? w.end : item.display.to,
						originalWord: w.originalWord,
						isEdited: w.isEdited,
					});
				}
			} else if (item.details?.text) {
				const textWords = item.details.text.trim().split(/\s+/).filter(Boolean);
				const dur = Math.max(1, item.display.to - item.display.from);
				const wordDur = dur / Math.max(1, textWords.length);
				textWords.forEach((tw: string, idx: number) => {
					allWords.push({
						word: tw,
						start: Math.round(item.display.from + idx * wordDur),
						end: Math.round(item.display.from + (idx + 1) * wordDur),
					});
				});
			}
		}

		if (allWords.length > 0) {
			const chunks: Array<typeof allWords> = [];
			let currentChunk: typeof allWords = [];

			for (let i = 0; i < allWords.length; i++) {
				const w = allWords[i]!;
				currentChunk.push(w);
				const isSentenceEnd =
					/[.!?]$/.test(w.word.trim()) || /[.!?]/.test(w.word.slice(-1));
				const nextWord = allWords[i + 1];
				const hasSilenceGap = nextWord ? nextWord.start - w.end > 1500 : false;

				if (currentChunk.length >= maxWords || isSentenceEnd || hasSilenceGap) {
					chunks.push(currentChunk);
					currentChunk = [];
				}
			}
			if (currentChunk.length > 0) {
				chunks.push(currentChunk);
			}

			const sample = existingItems[0];
			const sampleDetails = sample?.details ? { ...sample.details } : {};

			newTrackItems = chunks.map((chunk, index) => {
				const startMs = chunk[0]!.start;
				const endMs = chunk[chunk.length - 1]!.end;
				const nextChunk = chunks[index + 1];
				let adjustedEndTime = endMs;
				if (
					nextChunk &&
					nextChunk[0]!.start > startMs &&
					nextChunk[0]!.start - endMs <= 2000
				) {
					adjustedEndTime = nextChunk[0]!.start;
				} else {
					adjustedEndTime = Math.max(endMs, startMs + 300);
				}

				const text = chunk.map((w) => w.word).join(" ");
				const duration = Math.max(1, adjustedEndTime - startMs);

				return {
					id: generateId(10),
					name: `Caption ${index + 1}`,
					type: "caption",
					display: {
						from: startMs,
						to: adjustedEndTime,
					},
					duration,
					metadata: sample?.metadata ?? {},
					details: {
						...sampleDetails,
						text,
						words: [...chunk],
					},
				} as ITrackItem;
			});
		}
	}

	const updatedTrack: ITrack = {
		...captionTrack,
		items: newTrackItems.map((item) => item.id),
		maxWordsInPage: maxWords,
	};

	const updatedTrackItemsMap = { ...trackItemsMap };
	for (const id of removedItemIds) {
		delete updatedTrackItemsMap[id];
	}
	for (const item of newTrackItems) {
		updatedTrackItemsMap[item.id] = item;
	}

	const updatedTrackItemIds = Object.keys(updatedTrackItemsMap);

	return {
		updatedTrack,
		newTrackItems,
		removedItemIds,
		updatedTrackItemsMap,
		updatedTrackItemIds,
	};
}
